// ステータス画面モーダル（v0.6.0）。HUD の「庭 📊」ボタンで開く。
//
// 右上の生数値 HUD（#attrs）を廃止し、ここに庭の状態をグラフィカルにまとめる:
//  - 7 属性をアイコン＋ピクセル風の横バー（セグメント式・属性色）で表示。実効値で、
//    内訳（基礎=庭デコ / ボーナス=シナジー＋スペシャル）を色分け。
//  - 現在の季節（アイコン）・昼夜帯・スペシャル掲示中のドリンク。
//  - 評判レベル（★ゲージ）と同時滞在上限。
//  - 発動中の隣接シナジー一覧（アイコンペア＋効果）と、シナジールール一覧。
//
// DOM 直組み・タッチ一級。状態は持たず main から渡された値で描画するだけ。

import { ATTRIBUTE_KEYS, ATTRIBUTE_LABELS, type AttributeKey } from '../sim/decos';
import type { AttributeBreakdown } from '../sim/garden';
import { SPECIAL_LIGHT_COLORS } from '../palette';
import { SEASON_LABELS, SEASON_ICONS, type Season } from '../sim/season';
import { TIME_OF_DAY_LABELS, TIME_OF_DAY_ICONS, type TimeOfDay } from '../sim/daynight';
import { SYNERGY_RULES, type ActiveSynergy } from '../sim/synergy';
import { getDrink } from '../sim/menu';
import { getDeco } from '../sim/decos';
import {
  reputation,
  maxConcurrent,
  MAX_ARRIVAL_MULTIPLIER,
  arrivalMultiplier,
} from '../sim/reputation';
import type { GameState } from '../sim/state';
import { completionProgress, type CompletionAxis } from '../sim/completion';

/** ステータス画面に渡すスナップショット。 */
export interface StatusSnapshot {
  state: GameState;
  breakdown: AttributeBreakdown;
  synergies: ActiveSynergy[];
  season: Season;
  tod: TimeOfDay;
}

export interface StatusModal {
  open(snap: StatusSnapshot): void;
  close(): void;
  readonly isOpen: boolean;
}

export interface StatusModalOptions {
  modalEl: HTMLElement;
  bodyEl: HTMLElement;
}

/** バーの最大セグメント数（属性値がこれを超えても満タン表示で頭打ち）。 */
const BAR_SEGMENTS = 8;

/** 属性 1 行（アイコン＋ラベル＋セグメントバー）を作る。 */
function attrRow(key: AttributeKey, base: number, bonus: number): HTMLElement {
  const color = SPECIAL_LIGHT_COLORS[key] ?? '#FFB870';
  const total = Math.round(base + bonus);
  const baseSeg = Math.min(BAR_SEGMENTS, Math.round(base));
  const bonusSeg = Math.min(BAR_SEGMENTS - baseSeg, Math.round(bonus));

  const row = document.createElement('div');
  row.className = 'st-attr';

  const label = document.createElement('div');
  label.className = 'st-attr-label';
  label.textContent = ATTRIBUTE_LABELS[key];

  const bar = document.createElement('div');
  bar.className = 'st-bar';
  for (let i = 0; i < BAR_SEGMENTS; i++) {
    const seg = document.createElement('span');
    seg.className = 'st-seg';
    if (i < baseSeg) {
      seg.style.background = color;
    } else if (i < baseSeg + bonusSeg) {
      // ボーナスぶんは明るいハイライト枠で区別（シナジー/スペシャル由来）。
      seg.style.background = color;
      seg.classList.add('bonus');
    }
    bar.appendChild(seg);
  }

  const num = document.createElement('div');
  num.className = 'st-attr-num';
  num.textContent = bonus > 0 ? `${total}（+${Math.round(bonus)}）` : `${total}`;

  row.append(label, bar, num);
  return row;
}

/** 評判の★ゲージ（5 段階）を作る。 */
function repGauge(rep: number): HTMLElement {
  // 来店倍率の伸び（1.0..MAX）を 0..1 に正規化して★5 段階に量子化。
  const mult = arrivalMultiplier(rep);
  const frac = (mult - 1) / (MAX_ARRIVAL_MULTIPLIER - 1);
  const stars = Math.max(1, Math.min(5, Math.round(frac * 5)));
  const el = document.createElement('div');
  el.className = 'st-stars';
  el.textContent = '★'.repeat(stars) + '☆'.repeat(5 - stars);
  return el;
}

/** コンプ率（100%への道）ブロック。総合％ゲージ＋4軸（図鑑/島/ドリンク/強化）の達成。 */
function completionBlock(state: GameState): HTMLElement {
  const prog = completionProgress(state);
  const box = document.createElement('div');
  box.className = 'st-comp';

  const head = document.createElement('div');
  head.className = 'st-comp-head';
  head.innerHTML =
    `<span class="st-comp-title">${prog.done ? '🏆 コンプ達成！' : '🎯 コンプ率'}</span>` +
    `<span class="st-comp-pct">${prog.percent}%</span>`;
  box.appendChild(head);

  // 総合バー。
  const bar = document.createElement('div');
  bar.className = 'st-comp-bar';
  const fill = document.createElement('span');
  fill.className = 'st-comp-fill';
  fill.style.width = `${Math.round(prog.fraction * 100)}%`;
  bar.appendChild(fill);
  box.appendChild(bar);

  // 4 軸の内訳（アイコン＋値/目標、達成はチェック）。
  const grid = document.createElement('div');
  grid.className = 'st-comp-axes';
  for (const ax of prog.axes) grid.appendChild(axisChip(ax));
  box.appendChild(grid);

  return box;
}

/** コンプ 1 軸のチップ（達成済みは✓・色付き）。 */
function axisChip(ax: CompletionAxis): HTMLElement {
  const el = document.createElement('div');
  el.className = 'st-comp-axis' + (ax.done ? ' done' : '');
  el.innerHTML =
    `<span class="ic">${ax.icon}</span>` +
    `<span class="nm">${ax.label}</span>` +
    `<span class="vv">${ax.done ? '✓' : `${ax.value}/${ax.total}`}</span>`;
  return el;
}

export function createStatusModal(opts: StatusModalOptions): StatusModal {
  const { modalEl, bodyEl } = opts;

  function render(snap: StatusSnapshot): void {
    const { state, breakdown, synergies, season, tod } = snap;
    bodyEl.innerHTML = '';

    // --- コンプ率（100%への道）---
    bodyEl.appendChild(completionBlock(state));

    // --- 環境（季節・昼夜・スペシャル）---
    const env = document.createElement('div');
    env.className = 'st-env';
    const special = state.menu.special ? getDrink(state.menu.special) : null;
    env.innerHTML =
      `<span class="chip">${SEASON_ICONS[season]} ${SEASON_LABELS[season]}</span>` +
      `<span class="chip">${TIME_OF_DAY_ICONS[tod]} ${TIME_OF_DAY_LABELS[tod]}</span>` +
      `<span class="chip">☕ ${special ? special.name : 'スペシャル未設定'}</span>`;
    bodyEl.appendChild(env);

    // --- 評判・同時滞在 ---
    const rep = reputation(state);
    const cap = maxConcurrent(rep);
    const repBox = document.createElement('div');
    repBox.className = 'st-rep';
    const repLabel = document.createElement('span');
    repLabel.className = 'st-rep-label';
    repLabel.textContent = '評判';
    repBox.append(repLabel, repGauge(rep));
    const seats = document.createElement('span');
    seats.className = 'st-seats';
    seats.textContent = `同時滞在 ${cap} 席`;
    repBox.appendChild(seats);
    bodyEl.appendChild(repBox);

    // --- 属性バー ---
    const h1 = document.createElement('h3');
    h1.textContent = '庭の属性';
    bodyEl.appendChild(h1);
    const attrWrap = document.createElement('div');
    attrWrap.className = 'st-attrs';
    let anyAttr = false;
    for (const key of ATTRIBUTE_KEYS) {
      const total = breakdown.total[key];
      if (total <= 0) continue;
      anyAttr = true;
      attrWrap.appendChild(attrRow(key, breakdown.base[key], breakdown.bonus[key]));
    }
    if (!anyAttr) {
      const empty = document.createElement('div');
      empty.className = 'st-empty';
      empty.textContent = '庭はまだ静か。デコを置いて属性を育てよう。';
      attrWrap.appendChild(empty);
    }
    bodyEl.appendChild(attrWrap);

    // --- 発動中シナジー ---
    const h2 = document.createElement('h3');
    h2.textContent = '隣接シナジー';
    bodyEl.appendChild(h2);
    const synWrap = document.createElement('div');
    synWrap.className = 'st-syn';
    if (synergies.length === 0) {
      const none = document.createElement('div');
      none.className = 'st-empty';
      none.textContent = '発動中のシナジーはまだない。';
      synWrap.appendChild(none);
    } else {
      // ルールごとに件数を集約して 1 行ずつ。
      const byRule = new Map<string, { syn: ActiveSynergy; n: number }>();
      for (const s of synergies) {
        const e = byRule.get(s.ruleId);
        if (e) e.n++;
        else byRule.set(s.ruleId, { syn: s, n: 1 });
      }
      for (const { syn, n } of byRule.values()) {
        const rule = SYNERGY_RULES.find((r) => r.id === syn.ruleId);
        if (!rule) continue;
        const d1 = getDeco(rule.pair[0]);
        const d2 = getDeco(rule.pair[1]);
        const row = document.createElement('div');
        row.className = 'st-syn-row active';
        const total = syn.amount * n;
        row.innerHTML =
          `<span class="ic">${d1?.icon ?? '◻'}${d2?.icon ?? '◻'}</span>` +
          `<span class="nm">${rule.name}</span>` +
          `<span class="ef" style="color:${SPECIAL_LIGHT_COLORS[syn.attribute] ?? '#FFB870'}">` +
          `${ATTRIBUTE_LABELS[syn.attribute]} +${total}${n > 1 ? `（×${n}）` : ''}</span>`;
        synWrap.appendChild(row);
      }
    }
    bodyEl.appendChild(synWrap);

    // --- シナジールール一覧（常時公開）---
    const h3 = document.createElement('h3');
    h3.textContent = 'シナジー図鑑';
    bodyEl.appendChild(h3);
    const ruleWrap = document.createElement('div');
    ruleWrap.className = 'st-rules';
    const activeIds = new Set(synergies.map((s) => s.ruleId));
    for (const rule of SYNERGY_RULES) {
      const d1 = getDeco(rule.pair[0]);
      const d2 = getDeco(rule.pair[1]);
      const row = document.createElement('div');
      row.className = 'st-rule' + (activeIds.has(rule.id) ? ' on' : '');
      row.innerHTML =
        `<span class="ic">${d1?.icon ?? '◻'}＋${d2?.icon ?? '◻'}</span>` +
        `<span class="nm">${rule.name}</span>` +
        `<span class="ef">${ATTRIBUTE_LABELS[rule.attribute]} +${rule.amount}` +
        `${rule.growthBoost ? ' ＆ 成長↑' : ''}</span>`;
      ruleWrap.appendChild(row);
    }
    bodyEl.appendChild(ruleWrap);
  }

  function open(snap: StatusSnapshot): void {
    render(snap);
    modalEl.classList.add('open');
  }
  function close(): void {
    modalEl.classList.remove('open');
  }

  modalEl.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t === modalEl || t.hasAttribute('data-close')) close();
  });

  return {
    open,
    close,
    get isOpen() {
      return modalEl.classList.contains('open');
    },
  };
}
